import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, Activity, Clock, AlertCircle } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import API from '../api';
import { useActiveOrg } from '../contexts/ActiveOrgContext';

const StatCard = ({ title, value, icon: Icon, color }) => (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div className="flex items-center justify-between">
            <div>
                <p className="text-sm font-medium text-gray-500">{title}</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{value}</p>
            </div>
            <div className={`p-3 rounded-lg ${color}`}>
                <Icon className="w-6 h-6 text-white" />
            </div>
        </div>
    </div>
);

const Dashboard = () => {
    const { t, i18n } = useTranslation();
    const { activeOrg } = useActiveOrg();
    const [stats, setStats] = useState({
        totalRobots: 0,
        activeJobs: 0,
        completedJobs: 0,
        failedJobs: 0
    });
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchStats();
    }, [activeOrg]); // activeOrg değişince yeniden fetch

    const fetchStats = async () => {
        try {
            const [robotsRes, jobsRes, logsRes] = await Promise.all([
                API.get('/robots/'),
                API.get('/jobs/'),
                API.get('/logs/')
            ]);

            // Organizasyona göre filtrele
            let filteredRobots = robotsRes.data;
            let filteredJobs = jobsRes.data;
            let filteredLogs = logsRes.data;

            if (activeOrg) {
                filteredRobots = robotsRes.data.filter(robot =>
                    robot.organization === activeOrg.id || robot.organization_name === activeOrg.name
                );
                const orgRobotIds = filteredRobots.map(r => r.id);
                filteredJobs = jobsRes.data.filter(job => orgRobotIds.includes(job.robot));
                filteredLogs = logsRes.data.filter(log =>
                    filteredJobs.some(j => j.id === log.job)
                );
            }

            setStats({
                totalRobots: filteredRobots.length,
                activeJobs: filteredJobs.filter(j => !j.is_paused).length,
                completedJobs: filteredLogs.filter(l => l.status === 'success').length,
                failedJobs: filteredLogs.filter(l => l.status === 'error').length
            });

            // Son 7 günün grafik verilerini hesapla (TR saat dilimine göre)
            const dayMap = i18n.language && i18n.language.startsWith('en') ? {
                'Mon': 'Mon',
                'Tue': 'Tue',
                'Wed': 'Wed',
                'Thu': 'Thu',
                'Fri': 'Fri',
                'Sat': 'Sat',
                'Sun': 'Sun'
            } : {
                'Mon': 'Pzt',
                'Tue': 'Sal',
                'Wed': 'Çar',
                'Thu': 'Per',
                'Fri': 'Cum',
                'Sat': 'Cmt',
                'Sun': 'Paz'
            };
            const graphData = [];
            
            for (let i = 6; i >= 0; i--) {
                const d = new Date();
                d.setDate(d.getDate() - i);
                
                const dateStr = d.toLocaleDateString('sv-SE', { timeZone: 'Europe/Istanbul' });
                const weekdayShort = d.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'Europe/Istanbul' });
                const dayName = dayMap[weekdayShort] || weekdayShort;
                
                // Sadece başarılı çalışan işleri sayıyoruz (status === 'success')
                const count = filteredLogs.filter(log => {
                    if (!log.started_at || log.status !== 'success') return false;
                    const logDate = new Date(log.started_at);
                    const logDateStr = logDate.toLocaleDateString('sv-SE', { timeZone: 'Europe/Istanbul' });
                    return logDateStr === dateStr;
                }).length;
                
                graphData.push({
                    name: dayName,
                    jobs: count
                });
            }
            
            setChartData(graphData);
            setLoading(false);
        } catch (err) {
            console.error("Dashboard stats error:", err);
            setLoading(false);
        }
    };

    if (loading) return <div className="p-8 text-center text-gray-500">{t('common.loading')}</div>;

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-gray-900">{t('dashboard.title')}</h1>
                <p className="text-gray-500 mt-1">{t('dashboard.subtitle')}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard title={t('dashboard.total_robots')} value={stats.totalRobots} icon={Users} color="bg-blue-500" />
                <StatCard title={t('dashboard.active_jobs')} value={stats.activeJobs} icon={Activity} color="bg-green-500" />
                <StatCard title={t('dashboard.completed_jobs')} value={stats.completedJobs} icon={Clock} color="bg-purple-500" />
                <StatCard title={t('dashboard.failed_jobs')} value={stats.failedJobs} icon={AlertCircle} color="bg-red-500" />
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <h2 className="text-lg font-bold text-gray-900 mb-4">{t('dashboard.weekly_activity')}</h2>
                <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData}>
                            <defs>
                                <linearGradient id="colorJobs" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} />
                                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="name" />
                            <YAxis allowDecimals={false} />
                            <Tooltip />
                            <Area type="monotone" dataKey="jobs" stroke="#3b82f6" fillOpacity={1} fill="url(#colorJobs)" />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
